import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../lib/api';
import { authApi, inviteUrl, type OrgInvite, type OrgMemberView } from '../lib/authApi';
import { useAuth } from '../auth/AuthContext';
import { Popover } from '../ui/controls';
import { Icon } from '../ui/icons';

const ROLE_LABEL: Record<string, string> = { admin: 'Админ', member: 'Участник' };

/** The server's refusal in words, or the generic message for anything else. */
function refusalMessage(e: unknown, fallback: string): string {
  if (e instanceof ApiError) {
    if (e.code === 'unreachable') return e.message;
    if (e.code === 'super_admin_only') {
      return 'Назначать и снимать админов может только суперадминистратор';
    }
    if (e.code === 'last_admin') return 'Нельзя оставить организацию без админа';
  }
  return fallback;
}

/**
 * Org-admin-only popover: who is in the organisation, and a link to bring in
 * more people. Only rendered for an org where the caller is `admin` (or a
 * super admin) — `OrgSidebar` decides that before mounting this.
 */
export function OrgMembersPanel({
  orgId,
  orgName,
  onChanged,
}: {
  orgId: string;
  orgName: string;
  /** Called after a member's role changes or they are removed. */
  onChanged?: () => void;
}) {
  const { user } = useAuth();
  const [members, setMembers] = useState<OrgMemberView[]>([]);
  const [invites, setInvites] = useState<OrgInvite[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inviteRole, setInviteRole] = useState<'member' | 'admin'>('member');
  const [copiedToken, setCopiedToken] = useState<string | null>(null);
  const [lastUrl, setLastUrl] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [memberList, inviteList] = await Promise.all([
        authApi.listMembers(orgId),
        authApi.listInvites(orgId),
      ]);
      setMembers(memberList);
      setInvites(inviteList);
    } catch {
      setError('Не удалось загрузить участников');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    void load();
  }, [load]);

  const createLink = async () => {
    try {
      const invite = await authApi.createInvite(orgId, inviteRole);
      setInvites((current) => [invite, ...current]);
      await copyLink(invite.token);
    } catch {
      setError('Не удалось создать приглашение');
    }
  };

  const copyLink = async (token: string) => {
    const url = inviteUrl(token);
    setLastUrl(url);
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // Clipboard blocked: the URL is shown below to copy by hand.
    }
    setCopiedToken(token);
    setTimeout(() => setCopiedToken((t) => (t === token ? null : t)), 1600);
  };

  const revoke = async (token: string) => {
    try {
      await authApi.revokeInvite(orgId, token);
      setInvites((current) => current.filter((i) => i.token !== token));
    } catch {
      setError('Не удалось отозвать приглашение');
    }
  };

  const changeRole = async (userId: string, role: 'admin' | 'member') => {
    try {
      const updated = await authApi.setMemberRole(orgId, userId, role);
      setMembers((current) => current.map((m) => (m.userId === userId ? { ...m, role: updated.role } : m)));
      setError(null);
      onChanged?.();
    } catch (e) {
      setError(refusalMessage(e, 'Не удалось изменить роль'));
    }
  };

  const removeMember = async (userId: string) => {
    if (!window.confirm('Убрать этого участника из организации?')) return;
    try {
      await authApi.removeMember(orgId, userId);
      setMembers((current) => current.filter((m) => m.userId !== userId));
      setError(null);
      onChanged?.();
    } catch (e) {
      setError(refusalMessage(e, 'Не удалось убрать участника'));
    }
  };

  return (
    <Popover
      align="right"
      title={`Участники «${orgName}»`}
      trigger={() => <Icon name="users" size={15} />}
    >
      {() => (
        <div className="popover-stack org-members-popover">
          <div className="popover-label">Участники</div>

          {loading && <div className="org-members-hint">Загрузка…</div>}
          {error && <div className="org-members-error">{error}</div>}

          {!loading && (
            <ul className="org-members-list">
              {members.map((member) => (
                <li key={member.userId} className="org-member-row">
                  <span className="org-member-avatar" style={{ background: member.color }}>
                    {member.name.slice(0, 1).toUpperCase()}
                  </span>
                  <span className="org-member-text">
                    <span className="org-member-name">
                      {member.name}
                      {member.userId === user?.id && ' (вы)'}
                    </span>
                    <span className="org-member-email">{member.email}</span>
                  </span>
                  <select
                    className="org-member-role"
                    value={member.role}
                    onChange={(e) => void changeRole(member.userId, e.target.value as 'admin' | 'member')}
                  >
                    <option value="admin">Админ</option>
                    <option value="member">Участник</option>
                  </select>
                  <button
                    type="button"
                    className="icon-btn"
                    aria-label="Убрать из организации"
                    data-tip="Убрать"
                    onClick={() => void removeMember(member.userId)}
                  >
                    <Icon name="close" size={13} />
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className="panel-divider" />
          <div className="popover-label">Пригласить ссылкой</div>

          <div className="org-invite-create">
            <select
              className="org-member-role"
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value as 'admin' | 'member')}
            >
              <option value="member">Участник</option>
              <option value="admin">Админ</option>
            </select>
            <button type="button" className="menu-item org-invite-btn" onClick={() => void createLink()}>
              <Icon name="link" size={15} /> Создать ссылку
            </button>
          </div>

          {lastUrl && (
            <input
              className="tag-input"
              readOnly
              value={lastUrl}
              aria-label="Ссылка-приглашение"
              onFocus={(e) => e.currentTarget.select()}
            />
          )}

          {invites.length > 0 && (
            <ul className="org-invite-list">
              {invites.map((invite) => (
                <li key={invite.token} className="org-invite-row">
                  <button type="button" className="menu-item" onClick={() => void copyLink(invite.token)}>
                    <Icon name={copiedToken === invite.token ? 'check' : 'link'} size={14} />
                    <span>{ROLE_LABEL[invite.role]}-ссылка</span>
                  </button>
                  <button
                    type="button"
                    className="icon-btn"
                    aria-label="Отозвать ссылку"
                    data-tip="Отозвать"
                    onClick={() => void revoke(invite.token)}
                  >
                    <Icon name="trash" size={13} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Popover>
  );
}
